import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Banner from "../../Components/Banner";
import { ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import ServiceNowLeadForm from "../../Components/ServiceNowLeadForm";

function DatabricksManagedServices() {
  const approachData = [
    { id: 1, title: "Operational Reliability", desc: "Monitor jobs, pipelines, models, and platform health with clear alerting, ownership, and agreed response processes." },
    { id: 2, title: "Cost Visibility and FinOps", desc: "Understand spend by workspace, team, job, and user, then prioritize practical optimization opportunities." },
    { id: 3, title: "Secure Platform Administration", desc: "Manage workspaces, permissions, cluster policies, and Unity Catalog controls with consistent operational practices." },
    { id: 4, title: "Continuous Improvement", desc: "Review service performance, incidents, consumption, and enhancement priorities in transparent monthly reports." },
  ];

  const deliverables = [
    { title: "24x7 Monitoring and Support", desc: "Monitor job failures, missed deadlines, platform incidents, and operational alerts against agreed service levels and response times." },
    { title: "Platform Administration", desc: "Support workspaces, users, access requests, cluster policies, Unity Catalog configuration, and routine platform maintenance." },
    { title: "Pipeline and Model Operations", desc: "Coordinate reruns and fixes, monitor data freshness, and track model health and drift where applicable." },
    { title: "Release and Security Operations", desc: "Plan runtime upgrades, review access, monitor audit logs, and help remediate operational security issues." },
    { title: "Databricks Cost Optimization", desc: "Review compute and SQL warehouse sizing, serverless adoption, auto-termination, tagging, budgets, and query efficiency." },
    { title: "Enhancements and Expert Capacity", desc: "Provide ongoing capacity for new pipelines, dashboards, platform improvements, and approved AI use cases." },
  ];

  const deliverySteps = [
    { title: "Assess Platform and Priorities", duration: "Phase 1", desc: "Review architecture, workloads, access controls, incidents, service expectations, and current consumption to establish a practical operating baseline." },
    { title: "Agree on Service Levels", duration: "Phase 2", desc: "Define coverage, severity levels, response and escalation paths, responsibilities, reporting, and change-management practices." },
    { title: "Stabilize Operations", duration: "Transition", desc: "Set up monitoring, alerts, runbooks, access workflows, backup and recovery considerations, and handover from existing teams." },
    { title: "Operate and Optimize", duration: "Ongoing", desc: "Manage incidents and routine administration while reviewing spend, reliability, security, and platform performance." },
    { title: "Report and Improve", duration: "Monthly", desc: "Share service metrics, root causes, cost trends, completed improvements, and a prioritized plan for the next period." },
  ];

  const expertiseData = [
    { title: "Platform Monitoring and Incident Response", desc: "Track job failures, missed SLAs, workload health, and operational signals with clear ownership, escalation, and recovery runbooks.", image: "/assets/home-/ai-2.png", alt: "Databricks platform monitoring and incident response" },
    { title: "Workspace and Access Administration", desc: "Manage workspace settings, users, groups, cluster policies, and governed data access using consistent operational controls.", image: "/assets/home-/ai-3.png", alt: "Databricks workspace and access administration" },
    { title: "Pipeline and Model Operations", desc: "Support reliable pipeline execution, data freshness checks, model monitoring, and timely investigation of recurring issues.", image: "/assets/home-/ai-4.png", alt: "Data pipeline and machine learning model operations" },
    { title: "FinOps and Cost Optimization", desc: "Analyze consumption patterns, right-size compute and SQL warehouses, and identify opportunities to reduce avoidable spend.", image: "/assets/home-/img1.png", alt: "Databricks FinOps and cloud cost optimization" },
    { title: "Release and Security Management", desc: "Plan supported runtime upgrades, review audit signals, coordinate access reviews, and help teams adopt platform changes safely.", image: "/assets/home-/img2.png", alt: "Databricks release and security management" },
    { title: "Enhancements and Service Reporting", desc: "Extend platform capabilities while reporting reliability, incident trends, consumption, and continuous-improvement actions.", image: "/assets/home-/img3.png", alt: "Managed services reporting and platform enhancements" },
  ];

  const faqData = [
    { id: 1, question: "What are Databricks managed services?", answer: "Databricks managed services provide ongoing operational support for your platform, which can include monitoring, incident response, workspace administration, pipeline operations, security reviews, and cost optimization based on the agreed scope." },
    { id: 2, question: "Do you provide 24x7 support?", answer: "Monitoring and support coverage can be structured around your operational needs and agreed service levels. Coverage hours, response targets, escalation paths, and responsibilities should be defined during onboarding." },
    { id: 3, question: "Can you help reduce our Databricks costs?", answer: "Yes. We review workload consumption, compute and SQL warehouse sizing, serverless options, auto-termination, query efficiency, tagging, and budgets to identify and prioritize cost improvements. Savings depend on your current setup and workload patterns." },
    { id: 4, question: "Will you manage our existing pipelines and models?", answer: "We can support existing pipelines and model operations, including monitoring, troubleshooting, reruns, data freshness checks, and agreed maintenance activities after reviewing your architecture and runbooks." },
    { id: 5, question: "How does onboarding work?", answer: "Onboarding typically starts with a platform and workload assessment, followed by agreement on service levels, access and escalation procedures, monitoring, runbooks, and a structured handover from your current team." },
    { id: 6, question: "Can Mobikode support the platform after launch?", answer: "Yes. Ongoing support can include platform administration, pipeline monitoring, enhancements, cost optimization, and operational reporting based on an agreed engagement model." },
  ];

  const [deliveryActiveIndex, setDeliveryActiveIndex] = useState(0);
  const [expertiseActiveIndex, setExpertiseActiveIndex] = useState(0);
  const [faqActiveIndex, setFaqActiveIndex] = useState(0);

  const toggleFaqAccordion = (index) => {
    setFaqActiveIndex(faqActiveIndex === index ? -1 : index);
  };

  return (
    <div>
      <Helmet>
        <title>Databricks Managed Services &amp; Cost Optimization | Mobikode</title>
        <meta
          name="description"
          content="24x7 Databricks operations and FinOps from a Databricks Partner. Keep pipelines, models and agents reliable while optimizing platform spend."
        />
        <meta
          name="keywords"
          content="Databricks managed services, Databricks support, Databricks cost optimization, Databricks FinOps, platform operations"
        />
        <link
          rel="canonical"
          href="https://www.mobikode.com/data-ai-databricks-solutions/databricks-managed-services/"
        />
        <meta
          property="og:title"
          content="Databricks Managed Services &amp; Cost Optimization | Mobikode"
        />
        <meta
          property="og:description"
          content="Run and optimize Databricks with platform monitoring, operational support, security practices, and cost management."
        />
        <meta
          property="og:url"
          content="https://www.mobikode.com/data-ai-databricks-solutions/databricks-managed-services/"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Mobikode" />
        <meta
          property="og:image"
          content="https://www.mobikode.com/assets/services/bg3.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Databricks Managed Services &amp; Cost Optimization | Mobikode"
        />
        <meta
          name="twitter:description"
          content="Mobikode supports Databricks reliability, platform administration, pipeline operations, and ongoing FinOps."
        />
        <meta
          name="twitter:image"
          content="https://www.mobikode.com/assets/services/bg3.png"
        />
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative h-auto lg:h-[630px] w-full overflow-hidden bg-[#0a0b1e]">
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/services/bg3.png"
            alt="Databricks managed services and platform operations"
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-[#0a0b1e] via-[#0a0b1e]/80 to-transparent opacity-90" />
        </div>

        <div className="relative z-10 flex h-full max-w-[1440px] w-full mx-auto flex-col justify-between px-6 lg:px-20 pt-20 pb-[60px]">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:mt-[100px] mt-[40px]"
          >
            <p className="text-[#fff] font-graphik text-sm font-semibold uppercase tracking-[0.16em] mb-4">
              Databricks Partner · Managed Services
            </p>
            <h1 className="text-[38px] lg:text-[55px] text-white max-w-[800px] w-full font-sora font-semibold leading-[1.2] mb-[22px]">
              Your Databricks platform, run like it matters.
            </h1>
            <p className="text-white font-graphik text-[16px] md:text-[20px] font-normal leading-[1.6] mb-6 max-w-[760px]">
              We run, monitor, and optimize Databricks so your data and AI stay reliable while platform costs remain visible and controlled.
            </p>
            {/* <a
              href="#managed-services-lead-form"
              className="inline-flex items-center gap-2 bg-[#ED6800] hover:bg-[#d95f00] text-white font-graphik font-semibold px-6 py-3 transition-colors duration-300"
            >
              Get a free cost review <ChevronRight size={18} />
            </a> */}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-10 lg:self-end max-w-full lg:max-w-[501px] lg:ml-auto text-left"
          >
            <p className="text-white/90 font-graphik text-[14px] md:text-[16px] font-normal leading-[1.6]">
              Get dependable operations, clearer service ownership, and a practical plan for improving platform reliability and spend.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICE OVERVIEW */}
      <section className="w-full pt-16 lg:pt-[100px] bg-white overflow-hidden relative">
        <div className="max-w-[1440px] mx-auto px-[15px] lg:px-[80px]">
          <div className="relative w-full flex flex-col lg:flex-row lg:items-center group lg:min-h-[507px]">
            <div className="relative lg:absolute top-0 right-0 w-full lg:max-w-[656px] h-[250px] md:h-[350px] lg:h-full bg-gray-100 overflow-hidden">
              <img
                src="/assets/product/img3.png"
                alt="Databricks managed services and cost optimization"
                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="relative z-20 w-[92%] sm:w-[85%] lg:w-full max-w-[750px] mx-auto lg:mx-0 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] px-[20px] py-[30px] lg:px-[60px] lg:py-[70px] -mt-[40px] lg:mt-0">
              <p className="text-[#ED6800] text-xs font-semibold uppercase tracking-[0.15em] mb-3">
                From go-live to reliable operations
              </p>
              <h2 className="text-[#01386A] text-[20px] lg:text-[30px] font-semibold font-sora mb-[15px] lg:mb-[25px] leading-[1.3]">
                Keep your Databricks platform reliable, secure, and cost-aware.
              </h2>
              <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.7] font-medium font-graphik">
                After go-live, failed jobs, access requests, upgrades, and rising consumption can put pressure on internal teams. Mobikode provides structured operational support, monitoring, platform administration, and continuous cost optimization based on agreed service levels.
              </p>
              <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]" />
              <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY MOBIKODE */}
      <section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
          <div className="mb-[30px] lg:mb-[60px]">
            <p className="text-[#01386A] text-xs font-semibold uppercase tracking-[0.15em] mb-3">
              Why choose Mobikode for Databricks managed services
            </p>
            <h2 className="text-[#01386A] font-sora text-[24px] lg:text-[36px] font-semibold text-left">
              Keep workloads dependable and costs under control
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {approachData.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="relative bg-[#F0F0F0] p-8 min-h-[180px] flex flex-col justify-center overflow-hidden group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 rounded-sm"
              >
               
                <div className="relative z-10">
                  <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-semibold font-sora mb-2 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-[#49577A] text-[14px] lg:text-[16px] font-medium font-graphik leading-snug">
                    {item.desc}
                  </p>
                </div>
                <div className="absolute bottom-0 right-0 w-[180px] h-[140px] pointer-events-none transition-transform duration-500 group-hover:scale-110 opacity-40">
                  <img
                    src="/assets/career/wrap1.svg"
                    alt=""
                    className="w-full h-full object-contain object-bottom-right"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DELIVER */}
      <section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative">
        <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">
            <div className="w-full lg:w-[40%] lg:sticky lg:top-24">
              <p className="text-[#01386A] text-xs font-semibold uppercase tracking-[0.15em] mb-3">
                Managed services coverage
              </p>
              <h2 className="text-[#01386A] font-sora text-[24px] lg:text-[36px] font-semibold text-left mb-5">
                What your managed services plan can include
              </h2>
              <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed">
                From monitoring and platform administration to pipeline operations, security reviews, and FinOps, support is shaped around your workloads and service requirements.
              </p>
            </div>
            <div className="w-full lg:w-[60%] flex flex-col gap-6">
              {deliverables.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.45, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#ED6800] transition-colors duration-300 p-8 lg:px-10 lg:py-9 flex flex-col justify-center group"
                >
                  <div className="flex items-start gap-4">
                    {/* <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#F1F5F9] text-[#ED6800] font-semibold">
                      {String(index + 1).padStart(2, "0")}
                    </span> */}
                    <div>
                      <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-semibold font-sora mb-2">
                        {item.title}
                      </h3>
                      <p className="text-[#49577A] text-[14px] lg:text-[16px] font-medium font-graphik leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DELIVERY PROCESS */}
      <section className="w-full bg-white py-16 lg:py-24">
        <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
          <div className="mb-10 lg:mb-14 max-w-[760px]">
            <p className="text-[#01386A] text-xs font-semibold uppercase tracking-[0.15em] mb-3">
              A structured path to reliable operations
            </p>
            <h2 className="text-[#01386A] text-[24px] md:text-[36px] font-semibold font-sora mb-4">
              How we manage and improve your Databricks platform
            </h2>
            <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed">
              Each engagement starts with a clear operational baseline, agreed service levels, and a transition plan, then moves into monitoring, incident response, reporting, and continuous improvement.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
            <div className="lg:w-1/2 flex flex-col">
              {deliverySteps.map((step, index) => (
                <button
                  type="button"
                  key={step.title}
                  onClick={() => setDeliveryActiveIndex(index)}
                  className={`group cursor-pointer text-left flex items-center justify-between gap-4 py-6 lg:py-8 border-b-[3px] transition-all duration-300 ${
                    deliveryActiveIndex === index
                      ? "border-[#ED6800]"
                      : "border-gray-300 hover:border-gray-600"
                  }`}
                >
                  <div>
                    <span className="block text-[#01386A] text-xs font-semibold uppercase tracking-wider mb-2">
                      {step.duration}
                    </span>
                    <h3
                      className={`text-[16px] md:text-[20px] font-semibold font-sora transition-colors duration-300 ${
                        deliveryActiveIndex === index
                          ? "text-[#01386A]"
                          : "text-[#0000008C] group-hover:text-gray-600"
                      }`}
                    >
                      {step.title}
                    </h3>
                  </div>
                  <ChevronRight
                    size={24}
                    className={`flex-shrink-0 transition-all duration-300 ${
                      deliveryActiveIndex === index
                        ? "opacity-100 translate-x-0 text-[#ED6800]"
                        : "opacity-0 -translate-x-4 text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>

            <div className="lg:w-1/2 flex flex-col justify-start">
              <div className="min-h-[150px] mb-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={deliveryActiveIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-[#01386A] text-sm font-semibold mb-3">
                      STEP {String(deliveryActiveIndex + 1).padStart(2, "0")}
                    </p>
                    <h3 className="text-[#01386A] font-sora text-[22px] lg:text-[28px] font-semibold mb-4">
                      {deliverySteps[deliveryActiveIndex].title}
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-medium">
                      {deliverySteps[deliveryActiveIndex].desc}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="w-full h-[260px] lg:h-[340px] overflow-hidden relative bg-[#F1F5F9]">
                <img
                  src="/assets/home-/ai-2.png"
                  alt="Databricks managed services operating process"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/40 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPECTED OUTCOMES */}
      <section className="w-full bg-[#F7F9FC] py-16 lg:py-24">
        <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
          <div className="max-w-[760px] mb-10">
            <p className="text-[#01386A] text-xs font-semibold uppercase tracking-[0.15em] mb-3">
              Built around reliability and cost control
            </p>
            <h2 className="text-[#01386A] text-[24px] md:text-[36px] font-semibold font-sora mb-4">
              Operations that are reliable, transparent, and efficient
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                title: "Predictable Service Levels",
                desc: "Agree on service coverage, response targets, escalation paths, and ownership for operational incidents.",
              },
              {
                title: "Fewer Operational Disruptions",
                desc: "Monitor workloads and recurring failures to help teams detect issues earlier and restore service faster.",
              },
              {
                title: "Continuous Cost Optimization",
                desc: "Review consumption and workload configuration regularly to identify opportunities to manage platform spend.",
              },
              {
                title: "Clear Operational Reporting",
                desc: "Report on incidents, reliability, cost trends, and improvements so stakeholders can see how the service is performing.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white border border-[#E2E8F0] p-6 lg:p-7 hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-10 h-1 bg-[#ED6800] mb-5" />
                <h3 className="text-[#01386A] font-sora text-[17px] lg:text-[19px] font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-[#49577A] font-graphik text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULTING EXPERTISE TABS */}
      <section className="w-full bg-white py-16 lg:py-24">
        <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
          <div className="mb-10 lg:mb-14">
            <p className="text-[#01386A] text-xs font-semibold uppercase tracking-[0.15em] mb-3">
              Managed services capabilities
            </p>
            <h2 className="text-[#01386A] text-[24px] md:text-[36px] font-semibold font-sora mb-4">
              Databricks operations and FinOps expertise
            </h2>
            <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed max-w-[760px]">
              Explore the operational building blocks behind a dependable Databricks platform, from monitoring and access administration to cost optimization and service reporting.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
            <div className="lg:w-1/2 flex flex-col">
              {expertiseData.map((item, index) => (
                <button
                  type="button"
                  key={item.title}
                  onClick={() => setExpertiseActiveIndex(index)}
                  className={`group cursor-pointer flex items-center justify-between text-left gap-4 py-6 lg:py-7 border-b-[3px] transition-all duration-300 ${
                    expertiseActiveIndex === index
                      ? "border-[#ED6800]"
                      : "border-gray-300 hover:border-gray-600"
                  }`}
                >
                  <h3
                    className={`text-[16px] md:text-[20px] font-semibold font-sora transition-colors duration-300 ${
                      expertiseActiveIndex === index
                        ? "text-[#01386A]"
                        : "text-[#0000008C] group-hover:text-gray-600"
                    }`}
                  >
                    {item.title}
                  </h3>
                  <ChevronRight
                    size={24}
                    className={`flex-shrink-0 transition-all duration-300 ${
                      expertiseActiveIndex === index
                        ? "opacity-100 translate-x-0 text-[#ED6800]"
                        : "opacity-0 -translate-x-4 text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>

            <div className="lg:w-1/2 flex flex-col justify-start">
              <div className="min-h-[150px] mb-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={expertiseActiveIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 className="text-[#01386A] font-sora text-[22px] lg:text-[28px] font-semibold mb-4">
                      {expertiseData[expertiseActiveIndex % expertiseData.length].title}
                    </h3>
                    <p className="text-[#49577A] text-[14px] lg:text-[16px] leading-relaxed font-medium">
                      {expertiseData[expertiseActiveIndex % expertiseData.length].desc}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="w-full h-[280px] lg:h-[378px] overflow-hidden relative bg-[#F1F5F9]">
                <img
                  src={expertiseData[expertiseActiveIndex % expertiseData.length].image}
                  alt={expertiseData[expertiseActiveIndex % expertiseData.length].alt}
                  className="w-full h-full object-cover transition-opacity duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-100/40 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — LIGHT THEME MODERN DESIGN */}
      <section className="relative w-full overflow-hidden bg-[#fff] py-16 md:py-24 lg:py-28">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-16">
          {/* SECTION HEADING */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-12 max-w-[750px] text-center md:mb-16"
          >
            <span className="inline-block rounded-full border border-[#01386A]/10 bg-white px-5 py-2 font-graphik text-[11px] font-semibold uppercase tracking-[0.18em] text-[#01386A] shadow-sm">
              Databricks Managed Services Knowledge Hub
            </span>

            <h2 className="mt-6 font-sora text-[30px] font-semibold leading-[1.2] tracking-tight text-[#01386A] sm:text-[40px] lg:text-[48px]">
              Reliable operations start with clear ownership and visibility.
              <span className="mt-2 block text-[#ED6800]">
                
              </span>
            </h2>

            <p className="mx-auto mt-5 max-w-[590px] font-graphik text-[14px] leading-7 text-slate-600 md:text-[16px]">
              Get answers about Databricks support, onboarding, pipeline operations, platform security, and FinOps.
            </p>

            <div className="mx-auto mt-7 flex items-center justify-center gap-2">
              <span className="h-[3px] w-12 rounded-full bg-[#ED6800]" />
              <span className="h-[3px] w-3 rounded-full bg-[#01386A]/20" />
            </div>
          </motion.div>

          {/* FAQ LIST */}
          <div className="mx-auto max-w-[1000px]">
            {faqData.map((faq, index) => {
              const isActive = faqActiveIndex === index;

              return (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.4, delay: index * 0.04 }}
                  className={`mb-4 overflow-hidden rounded-xl border transition-all duration-300 ${
                    isActive
                      ? "border-[#01386A]/20 bg-white shadow-[0_8px_30px_rgba(1,56,106,0.07)]"
                      : "border-[#E7E8EC] bg-white/70 hover:border-[#01386A]/20 hover:bg-white"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleFaqAccordion(index)}
                    aria-expanded={isActive}
                    aria-controls={`managed-services-answer-${faq.id}`}
                    className="flex w-full items-center gap-4 px-4 py-5 text-left sm:gap-6 sm:px-7 sm:py-6"
                  >
                    {/* QUESTION */}
                    <div className="min-w-0 flex-1">
                      <span className="mb-1 block font-graphik text-[10px] font-semibold uppercase tracking-[0.15em] text-[#ED6800] sm:hidden">
                        Question {String(index + 1).padStart(2, "0")}
                      </span>

                      <h3
                        className={`font-sora text-[14px] font-semibold leading-6 transition-colors sm:text-[17px] ${
                          isActive ? "text-[#01386A]" : "text-slate-700"
                        }`}
                      >
                        {faq.question}
                      </h3>
                    </div>

                    {/* ACCORDION ICON */}
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                        isActive
                          ? "rotate-180 bg-[#ED6800] text-white"
                          : "border border-[#E5E7EB] bg-white text-[#01386A]"
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </span>
                  </button>

                  {/* ANSWER */}
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        id={`managed-services-answer-${faq.id}`}
                        key="answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          height: { duration: 0.3, ease: "easeInOut" },
                          opacity: { duration: 0.2 },
                        }}
                        className="overflow-hidden"
                      >
                        <div className="mx-4 mb-5 border-t border-dashed border-[#DDE3EB] pt-4 sm:mx-7 sm:mb-7 sm:ml-7 sm:mr-7 sm:pt-5">
                          <p className="font-graphik text-[13px] leading-7 text-slate-600 sm:text-[15px]">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* BOTTOM ACCENT */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-10 text-center md:mt-14"
          >
            <p className="font-graphik text-[12px] leading-6 text-slate-500 sm:text-[13px]">
              Keep Databricks dependable with clear service levels, proactive operations, and continuous cost reviews.
            </p>
          </motion.div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="managed-services-lead-form" className="scroll-mt-24">
        <ServiceNowLeadForm />
      </section>

      {/* CLOSING CTA */}
      <Banner
        title="Run Databricks with confidence."
        text="Get a cost and operations review to identify reliability gaps, operational risks, and practical optimization opportunities."
      />
    </div>
  );
}

export default DatabricksManagedServices;
